import { useCallback, useRef, useState } from 'react';
import { Button } from './Button.js';
import './VisuallyHidden.css';
import './CopyButton.css';

export interface CopyButtonProps {
  /** What lands on the clipboard. */
  value: string;
  /** The idle label. Defaults to `Copy`. */
  label?: string;
  /** Names *what* is being copied, for a screen reader — "Copy" alone says nothing on its own. */
  what?: string;
  size?: 'sm' | 'md';
}

/**
 * Copy a value to the clipboard, and **say whether it worked**.
 *
 * ⚠️ **The failure state is the reason this is a component rather than three lines at each call
 * site.** `navigator.clipboard.writeText` rejects — on an insecure origin, when the document is not
 * focused, and whenever the browser withholds the permission — and the overwhelmingly common
 * implementation swallows that and flips the label to "Copied" anyway. The user then pastes
 * whatever was on their clipboard before into a production agent's configuration. This component
 * distinguishes the three outcomes and never claims the third.
 *
 * ⚠️ **The confirmation is announced, not only coloured** (a11y §2): the label itself changes, and
 * the live region carries it for a screen reader, because a green tick is invisible to half the
 * ways this button gets used.
 */
export function CopyButton({ value, label = 'Copy', what, size = 'sm' }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    try {
      /**
       * ⚠️ **Checked for ABSENCE first, and the optional chain alone was the very bug this component
       * exists to prevent.** `navigator.clipboard?.writeText(...)` yields `undefined` on an insecure
       * origin, `await undefined` resolves, and the next line reported "Copied" — so on an http
       * origin the user pastes whatever was on their clipboard before into a production agent's
       * configuration, which is this file's own stated failure scenario. Only a *rejection*
       * (unfocused document, denied permission) reached the catch.
       */
      const clipboard = navigator.clipboard;
      if (!clipboard?.writeText) throw new Error('The clipboard API is not available on this page.');
      await clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    /* Back to idle, so a second copy of a *different* value never looks already-done. */
    timer.current = setTimeout(() => setState('idle'), 4000);
  }, [value]);

  const text = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed — select it instead' : label;

  return (
    <span className="dpr-copy">
      <Button
        variant="secondary"
        size={size}
        onClick={copy}
        aria-label={what ? `${text}: ${what}` : undefined}
      >
        {text}
      </Button>
      {/* Polite, not assertive: this confirms an action the user just took rather than interrupting. */}
      <span className="dpr-visually-hidden" role="status" aria-live="polite">
        {state === 'copied' ? `Copied${what ? ` ${what}` : ''}` : ''}
        {state === 'failed' ? 'Copy failed. Select the text and copy it yourself.' : ''}
      </span>
    </span>
  );
}
